import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GetUsersPaged, User, IPaginated } from '@bforms-web/common';
import { useLoading } from '../../../contexts/loading.context';
import UserTable from '../../UserTable';
import { useStyles } from './styles';

export type UserListProps = {
  getUsersPaged: GetUsersPaged;
  onDelete: (params: { id: string }) => Promise<void>;
};

type UserListState = {
  users: IPaginated<User>;
};

type FilterState = {
  page: number;
  rows: number;
  timeout: number;
};

const defaultState: UserListState = {
  users: {
    data: [],
    total: 0,
  }
};

const defaultFilterState: FilterState = {
  page: 0,
  rows: 10,
  timeout: 0,
};

export default function UserList({
  getUsersPaged,
  onDelete,
}: UserListProps): JSX.Element {
  const classes = useStyles();
  const [state, setState] = useState<UserListState>(defaultState);
  const [filterState, setFilterState] = useState<FilterState>(defaultFilterState);
  const { showLoading, hideLoading } = useLoading();

  useEffect(() => {
    const handler = setTimeout(() => {
    showLoading();
    (async () => {
      try {
        const users = await getUsersPaged.execute({
          programId: 'NoProgram', // Caso crie um vinculo entre usuários e programas
          filter: 'NoFilter',    // Caso seja implementado algum filtro na tela de usuários
          skip: filterState.page * filterState.rows,
          take: filterState.rows,
        });
        
        setState({
          users,
        });
      } catch (err) {
        console.log(err);
      } finally {
        hideLoading();
      }
    })();
    }, filterState.timeout);
    return () => clearTimeout(handler);
  }, [filterState]);

  const handlePageChange = useCallback((event: unknown, page: number) => {
    setFilterState(prev => ({
      ...prev,
      page,
      timeout: 200,
    }));
  }, []);

  const handleRowsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newRows = parseInt(event.target.value, 10) || 10;
    setFilterState((prevFilterState) => ({
      ...prevFilterState,
      rows: newRows,
      page: 0,
    }));
  }, []);

  const handleDelete = useCallback(async (user: User) => {
    if (window.confirm(`Tem certeza que deseja excluir o usuário ${user.name}?`)) {
      try {
        showLoading();
        await onDelete({ id: user.id });
        toast.success('Usuário excluído com sucesso!');
        setFilterState(prev => ({
          ...prev,
          timeout: 0,
        }));
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Erro ao excluir usuário');
      } finally {
        hideLoading();
      }
    }
  }, [onDelete, showLoading, hideLoading]);
  
  return (
    <Box className={classes.container}>
      <UserTable
        data={state.users.data}
        count={state.users.total}
        page={filterState.page}
        rowsPerPage={filterState.rows}
        onChangePage={handlePageChange}
        onChangeRows={handleRowsChange}
        onDelete={handleDelete}
      />
    </Box>
  );
}
